import React, { useEffect, useState } from 'react'
import { useAlerts, useDialog, DialogsRendererPropTypes } from '@theneatcompany/nui'
import { useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'
import Image from 'next/image'
import cx from 'classnames'
import { useRouter } from 'next/router'

import { APP_URL } from '@/config'
import { Link, Dialog, Text, Button } from '@/components/common'
import CheckboxField from '@/components/forms/CheckboxField'
import CouponForm from '@/components/Pages/SignUp/Coupon'
import { PAYMENT_METHOD_TYPES, useSignUp } from '@/components/Providers/SignUpProvider'
import Yup from '@/utils/yup'
import Form from '@/components/forms/Form'
import SelectField from '@/components/forms/SelectField'
import { Columns, Column, Rows, Box } from '@/components/layout'
import InputField from '@/components/forms/InputField'
import PhoneNumberField from '@/components/forms/PhoneNumberField'
import CheckoutSideCreditCard from '@/components/blocks/CheckoutSideCreditCard'
import CheckoutSideCreditCardTrial from '@/components/blocks/CheckoutSideCreditCardTrial'
import CheckoutSideCreditCardNeatHome from '@/components/blocks/CheckoutSideCreditCardNeatHome'
import Spinner from '@/components/common/FullScreenSpinner'
import badgeBbbImg from '@/assets/buy-flow/badge-bbb.png'
import badgeSslImg from '@/assets/buy-flow/badge-ssl.png'
import routes from '@/routes'

import PaymentMethods from './PaymentMethods'
import FormBox from './FormBox'
import SignUpLayout from './SignUpLayout'
import styles from './SignUp.module.scss'

const schema = Yup.object({
  promoCode: Yup.string().trim(),
  billingFirstName: Yup.string().trim().max(50).required(),
  billingLastName: Yup.string().trim().max(50).required(),
  billingCountry: Yup.string().trim().required(),
  billingZipCode: Yup.string().trim().required(),
  phone: Yup.string().trim().phoneNumber({ path: 'phone' }).required(),
  agreeToTnC: Yup.bool().required(),
})

const options = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
]

const commonInputProps = {
  size: 'large',
  helperProps: {
    'data-testid': 'billing-validation-error',
  },
}

const SubmitButton = ({ isTrial = false, disabled = false, className }) => {
  const { watch } = useFormContext()
  const agreedToTerms = watch('agreeToTnC', false)
  const paymentMethod = watch('paymentMethod', false)

  let submitMessage = 'submit'

  if (isTrial) { submitMessage = 'submitTrial' }
  if (paymentMethod === 'paypalPayment') { submitMessage = 'paypal' }

  return (
    <Form.Action
      className={cx(styles.submit, className)}
      data-testid="billing-finalize-purchase-cta"
      disabled={!agreedToTerms || disabled}
      messageId={`pages.signUp.form.billing.${submitMessage}`}
      size="xlarge"
      type="submit"
    />
  )
}

SubmitButton.propTypes = {
  isTrial: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

const FreeTrialDialog = ({ handleClick, ...rest }) => (
  <Dialog
    canOutsideClickClose
    onClose
    {...rest}
  >
    <Dialog.Content className="text-center">
      <Text.P>Oops! We encountered an unexpected error and our engineers have been alerted.
        If the problem persists, please contact Neat customer support for assistance with your purchase.
      </Text.P>
      <Text.P>In the meantime, we'd love for you to get started using Neat,
        so click below to start a 30 day no-cc trial now.
      </Text.P>
      <Button messageId="cta.noCcTrial" onClick={handleClick} size="xlarge" variant="success" />
    </Dialog.Content>
  </Dialog>
)

FreeTrialDialog.propTypes = {
  ...DialogsRendererPropTypes,
}

const TooManyAttemptsDialog = ({ handleClick, ...rest }) => (
  <Dialog
    canOutsideClickClose={false}
    onClose
    {...rest}
  >
    <Dialog.Content className="text-center">
      <Text.P>We’re sorry, there have been too many failed order attempts,
        no more attempts are allowed.  If you need assistance placing an order, please contact Neat support.
      </Text.P>
      <Button messageId="cta.tooManyAttempts" onClick={handleClick} />
    </Dialog.Content>
  </Dialog>
)

TooManyAttemptsDialog.propTypes = {
  ...DialogsRendererPropTypes,
}

const Billing = () => {
  const {
    user,
    plan,
    recurlyFormValidation,
    saveStepBilling,
    orderError,
    isOrderProcessing,
    isPayPalOpen,
    showNoCcTrial,
    handleCreateNoCcTrialOrder,
    showTooManyAttempts,
    setShowTooManyAttempts,
  } = useSignUp()
  const { push } = useRouter()

  const [showNoCcTrialActive, setShowNoCcTrialActive] = useState(false)

  const [openFreeTrialDialog, closeFreeTrialDialog] = useDialog(FreeTrialDialog)
  const [openTooManyAttemptsDialog, closeTooManyAttemptsDialog] = useDialog(TooManyAttemptsDialog)

  useEffect(() => {
    if (!showNoCcTrial || isOrderProcessing) {
      closeFreeTrialDialog()
      setShowNoCcTrialActive(false)
    } else if (showNoCcTrial && !showNoCcTrialActive) {
      openFreeTrialDialog({ handleClick: handleCreateNoCcTrialOrder })
      setShowNoCcTrialActive(true)
    }
  }, [showNoCcTrial, isOrderProcessing])

  const handleRedirectToHome = () => {
    push(routes.index)
    setShowTooManyAttempts(false)
  }

  useEffect(() => {
    if (showTooManyAttempts) {
      openTooManyAttemptsDialog({ handleClick: handleRedirectToHome })
    } else {
      closeTooManyAttemptsDialog()
    }
  }, [showTooManyAttempts])

  const alerts = useAlerts()

  const handleSubmit = (model) => {
    if (showTooManyAttempts) {
      showTooManyAttempts()
      return
    }
    alerts.clear()
    if (!model.agreeToTnC) {
      alerts.error('Please agree to the Terms and Conditions')
      return
    }

    saveStepBilling(model)
  }

  const getSideContent = () => {
    if (plan.sku === 'neathomeyr' || plan.sku === 'neathomevipyr') return <CheckoutSideCreditCardNeatHome />
    if (plan.isTrial) return <CheckoutSideCreditCardTrial />

    return <CheckoutSideCreditCard />
  }

  useEffect(() => {
    if (orderError && !isOrderProcessing) {
      alerts.error(orderError)
    }
  }, [orderError, isOrderProcessing])

  return (
    <>
      <SignUpLayout sideContent={getSideContent()}>
        <FormBox gap={0}>
          <CouponForm />

          <Form
            initialValues={{
              billingFirstName: user.firstName,
              billingLastName: user.lastName,
              billingCountry: 'US',
              billingZipCode: '',
              phone: '',
              agreeToTnC: false,
              paymentMethod: PAYMENT_METHOD_TYPES.ccPayment,
            }}
            onSubmit={handleSubmit}
            schema={schema}
          >
            <Column />
            <h1 className={styles.headlineBilling}>
              Billing Information
            </h1>
            <Rows gap={0}>
              <Columns gap={8} noMobileGap>
                <Column>
                  <InputField
                    data-recurly="first_name"
                    data-testid="billing-first-name-input-field"
                    intent={recurlyFormValidation.first_name === false ? 'danger' : undefined}
                    label="First name"
                    name="billingFirstName"
                    {...commonInputProps}
                  />
                </Column>
                <Column>
                  <InputField
                    data-recurly="last_name"
                    data-testid="billing-last-name-input-field"
                    intent={recurlyFormValidation.last_name === false ? 'danger' : undefined}
                    label="Last name"
                    name="billingLastName"
                    {...commonInputProps}
                  />
                </Column>
              </Columns>
            </Rows>
            <Column>
              <SelectField
                filterable={false}
                id="countrySelect"
                label="Country"
                name="billingCountry"
                options={options}
              />
            </Column>
            <Column>
              <InputField
                data-testid="billing-zip-input-field"
                intent={recurlyFormValidation.postal_code === false ? 'danger' : undefined}
                label="Zip Code"
                name="billingZipCode"
                {...commonInputProps}
              />
            </Column>
            <Column>
              <PhoneNumberField
                data-testid="mobile-number-input-field"
                label="Phone Number"
                name="phone"
                placeholder="(201) 555-0123"
                {...commonInputProps}
              />
            </Column>
            <h1 className={styles.headlineBilling}>
              How do you want to pay?
            </h1>
            <PaymentMethods />
            <CheckboxField
              className={styles.billingCheckbox}
              data-testid="billing-terms-checkbox"
              label={
                <>
                  <span className={styles.smallPrint}>I agree to the <Link size="xsmall" target="_blank" to={`${APP_URL}/terms-conditions/`}>Terms and Conditions.</Link></span>
                </>
                }
              name="agreeToTnC"
              size="small"
            />

            <SubmitButton
              className={cx(isOrderProcessing && styles.disabledVerifying)}
              disabled={isOrderProcessing}
              isTrial={plan?.isTrial}
            />
          </Form>
          <Box center gap={8} wrap>
            <Image alt="bbb accredited business" src={badgeBbbImg} />
            <Image alt="fully secured ssl checkout" src={badgeSslImg} />
          </Box>
        </FormBox>
      </SignUpLayout>
      {isOrderProcessing &&
      <Spinner><h2>Setting Up Your Account</h2>
        <p>Please wait...</p>
      </Spinner>}
      {isPayPalOpen &&
      <Spinner><h2>Please sign in to PayPal</h2>
        <p>PayPal will open in a new window</p>
      </Spinner>}
    </>
  )
}
export default Billing
